import { BREED_PALS, GENDER_SPECIALS } from "@/data/breedingPals";

export const N = BREED_PALS.length; // 299
export const PALS_BY_INDEX = BREED_PALS;

// Flat upper-triangle offset: base(i) = i*N - i*(i-1)/2
function base(i) {
  return i * N - (i * (i - 1)) / 2;
}
function cellIndex(a, b) {
  const i = Math.min(a, b);
  const j = Math.max(a, b);
  return base(i) + (j - i);
}

let MATRIX = null; // Int array of child indices (65535 = special/gendered)
let loadPromise = null;

export function loadMatrix() {
  if (MATRIX) return Promise.resolve(MATRIX);
  if (loadPromise) return loadPromise;
  loadPromise = fetch("/breeding_matrix.json")
    .then((r) => {
      if (!r.ok) throw new Error("matrix fetch failed");
      return r.json();
    })
    .then((arr) => {
      MATRIX = arr;
      return MATRIX;
    })
    .catch((e) => {
      loadPromise = null; // allow retry
      throw e;
    });
  return loadPromise;
}

export function isReady() {
  return !!MATRIX;
}

// Returns { child } for a normal pair, or { gendered: [{child, g1, g2}] } for the gender-special pair.
export function breed(a, b) {
  if (a == null || b == null) return null;
  const gs = GENDER_SPECIALS.filter(
    (g) => (g.p1 === a && g.p2 === b) || (g.p1 === b && g.p2 === a)
  );
  if (gs.length) {
    return {
      gendered: gs.map((g) => ({ child: g.child, p1: g.p1, g1: g.g1, p2: g.p2, g2: g.g2 })),
    };
  }
  const c = MATRIX[cellIndex(a, b)];
  if (c === 65535 || c === undefined) return null;
  return { child: c };
}

// Find every parent pair that yields `childIdx`.
// options: { ownedOnly, ownedSet, parentFilter }
export function findParents(childIdx, { ownedOnly = false, ownedSet = null, parentFilter = null } = {}) {
  if (childIdx == null || !MATRIX) return [];
  const pairs = [];
  for (let i = 0; i < N; i++) {
    if (ownedOnly && ownedSet && !ownedSet.has(i)) continue;
    if (parentFilter != null && i !== parentFilter) {
      // parentFilter must be one of the two parents; handle below
    }
    for (let j = i; j < N; j++) {
      if (ownedOnly && ownedSet && !ownedSet.has(j)) continue;
      if (parentFilter != null && i !== parentFilter && j !== parentFilter) continue;
      const c = MATRIX[cellIndex(i, j)];
      if (c === childIdx) pairs.push([i, j]);
    }
  }
  // gender specials
  for (const g of GENDER_SPECIALS) {
    if (g.child === childIdx) {
      if (ownedOnly && ownedSet && (!ownedSet.has(g.p1) || !ownedSet.has(g.p2))) continue;
      if (parentFilter != null && g.p1 !== parentFilter && g.p2 !== parentFilter) continue;
      pairs.push([g.p1, g.p2, { g1: g.g1, g2: g.g2 }]);
    }
  }
  // Sort: fewest-effort first — prefer pairs where parents are lower rarity / easier
  pairs.sort((x, y) => {
    const rx = (PALS_BY_INDEX[x[0]].rarity || 0) + (PALS_BY_INDEX[x[1]].rarity || 0);
    const ry = (PALS_BY_INDEX[y[0]].rarity || 0) + (PALS_BY_INDEX[y[1]].rarity || 0);
    return rx - ry;
  });
  return pairs;
}

export const OWNED_KEY = "palhub_owned_pals";

// Action plan optimised for what you OWN.
// Minimises the number of Pals you must still CAPTURE (missing), preferring paths that
// use owned Pals; falls back to capturing a Pal directly when breeding it isn't cheaper.
// Leaves are either { own:true } (POSSÉDÉ) or { capture:true } (MANQUANT).
// Returns { steps, tree, missing:[idx...], captures } or null.
export function ownedPlan(targetIdx, owned, maxGen = 7) {
  if (targetIdx == null || !MATRIX) return null;
  const isOwned = (i) => !!(owned && owned.has(i));
  const rarOf = (i) => (PALS_BY_INDEX[i] && PALS_BY_INDEX[i].rarity) || 1;
  // Capture cost weighted by rarity: capturing a legendary is far costlier than a common,
  // so the search prefers BREEDING up from owned / easy-to-catch ancestors (multi-gen).
  const leafCost = (i) => (isOwned(i) ? 0 : 1 + rarOf(i));
  const cost = new Array(N);
  const pairRar = new Array(N).fill(Infinity); // secondary key: summed rarity of chosen pair
  const bestPair = new Array(N).fill(null);
  for (let i = 0; i < N; i++) cost[i] = leafCost(i);

  for (let round = 0; round < maxGen; round++) {
    let changed = false;
    for (let i = 0; i < N; i++) {
      const ci = cost[i];
      if (ci === Infinity) continue;
      const bi = i;
      for (let j = i; j < N; j++) {
        const cj = cost[j];
        if (cj === Infinity) continue;
        const c = MATRIX[cellIndex(i, j)];
        if (c === 65535 || c === undefined || c === i || c === j) continue;
        if (isOwned(c)) continue;
        const cand = ci + cj;
        const pr = rarOf(i) + rarOf(j);
        if (cand < cost[c] || (cand === cost[c] && pr < pairRar[c])) {
          cost[c] = cand; pairRar[c] = pr; bestPair[c] = [i, j]; changed = true;
        }
      }
    }
    for (const g of GENDER_SPECIALS) {
      const c = g.child;
      if (isOwned(c)) continue;
      const cand = cost[g.p1] + cost[g.p2];
      const pr = rarOf(g.p1) + rarOf(g.p2);
      if (cand < cost[c] || (cand === cost[c] && pr < pairRar[c])) {
        cost[c] = cand; pairRar[c] = pr; bestPair[c] = [g.p1, g.p2]; changed = true;
      }
    }
    if (!changed) break;
  }

  const buildTree = (idx, seen) => {
    if (isOwned(idx)) return { idx, own: true, children: null };
    const pr = bestPair[idx];
    if (!pr || seen.has(idx)) return { idx, own: false, capture: true, children: null };
    const ns = new Set(seen); ns.add(idx);
    return { idx, own: false, children: [buildTree(pr[0], ns), buildTree(pr[1], ns)] };
  };
  const tree = buildTree(targetIdx, new Set());

  const missing = new Set();
  let steps = 0;
  const walk = (n) => {
    if (!n) return;
    if (n.children) { steps++; n.children.forEach(walk); }
    else if (n.capture) missing.add(n.idx);
  };
  walk(tree);
  return { steps, tree, missing: [...missing], captures: missing.size };
}

// Shortest multi-generation breeding plan from a set of starter pals to a target.
// starters: Set of pal indices you already have / can obtain. Returns a tree or null.
export function breedingPlan(targetIdx, starters) {
  if (targetIdx == null || !MATRIX || !starters || starters.size === 0) return null;
  const step = new Map();
  const parents = new Map();
  for (const s of starters) step.set(s, 0);
  if (step.has(targetIdx)) return { steps: 0, tree: { idx: targetIdx, own: true, children: null } };

  let level = 0;
  const MAX_LEVELS = 8;
  while (!step.has(targetIdx) && level < MAX_LEVELS) {
    const reached = [...step.keys()];
    const newly = new Map();
    for (let a = 0; a < reached.length; a++) {
      for (let b = a; b < reached.length; b++) {
        const i = reached[a], j = reached[b];
        const c = MATRIX[cellIndex(i, j)];
        if (c === 65535 || c === undefined) continue;
        if (step.has(c) || newly.has(c)) continue;
        newly.set(c, [i, j]);
      }
    }
    if (newly.size === 0) break;
    for (const [c, pr] of newly) { step.set(c, level + 1); parents.set(c, pr); }
    level++;
  }
  if (!step.has(targetIdx)) return null;

  const build = (idx) => {
    if (!parents.has(idx)) return { idx, own: starters.has(idx), children: null };
    const [a, b] = parents.get(idx);
    return { idx, own: false, children: [build(a), build(b)] };
  };
  return { steps: step.get(targetIdx), tree: build(targetIdx) };
}

// ---------------------------------------------------------------------------
// Breeding Path Finder — faithful reproduction of palworld.gg's algorithm.
// Given a TARGET pal and the set of pals you OWN, finds the SHORTEST breeding
// chain that starts ONLY from owned pals. Intermediate (bred) pals are flagged
// so the UI can highlight "bred in an earlier step". Never introduces an
// unowned pal as a starting material — this fixes the previous bug.
// ---------------------------------------------------------------------------

// All possible children of pairing a+b (covers gendered specials). Excludes a==b.
function childrenOf(a, b) {
  if (a === b) return [];
  const r = breed(a, b);
  if (!r) return [];
  if (r.gendered) return r.gendered.map((g) => g.child);
  return r.child != null ? [r.child] : [];
}

export function pathFinder(targetIdx, ownedSet) {
  if (targetIdx == null || !MATRIX || !ownedSet) return null;

  const alreadyOwned = ownedSet.has(targetIdx);
  // The target is never a valid starting material for its own chain — even if owned.
  const sources = new Set(ownedSet);
  sources.delete(targetIdx);
  if (sources.size < 2) return { steps: null, owned: alreadyOwned };

  const cost = new Map();
  const parent = new Map();
  for (const s of sources) cost.set(s, 0);

  const visited = new Set();
  const expanded = []; // nodes popped in cost order

  // Dijkstra: a child's cost = cost(parentA) + cost(parentB) + 1.
  for (;;) {
    let n = null;
    let best = Infinity;
    for (const [k, v] of cost) {
      if (!visited.has(k) && v < best) { best = v; n = k; }
    }
    if (n == null) break;
    visited.add(n);
    expanded.push(n);
    for (const g of expanded) {
      for (const child of childrenOf(n, g)) {
        if (visited.has(child) || child === targetIdx) continue; // don't relax target as an intermediate source
        const nc = cost.get(n) + cost.get(g) + 1;
        if (nc < (cost.get(child) ?? Infinity)) {
          cost.set(child, nc);
          parent.set(child, [n, g]);
        }
      }
    }
  }

  // Cheapest pair of reachable pals that produces the target (target excluded as parent).
  let bestPair = null;
  let bestCost = Infinity;
  for (let i = 0; i < expanded.length; i++) {
    for (let j = i; j < expanded.length; j++) {
      const g = expanded[i];
      const h = expanded[j];
      if (g === targetIdx || h === targetIdx) continue;
      if (!childrenOf(g, h).includes(targetIdx)) continue;
      const c = cost.get(g) + cost.get(h);
      if (c < bestCost) { bestCost = c; bestPair = [g, h]; }
    }
  }
  if (!bestPair) return { steps: null, owned: alreadyOwned }; // unreachable from owned pals

  // Reconstruct ordered steps (post-order: parents before child).
  const steps = [];
  const done = new Set();
  const rec = (node) => {
    const pr = parent.get(node);
    if (!pr || done.has(node)) return;
    done.add(node);
    rec(pr[0]);
    rec(pr[1]);
    steps.push({ a: pr[0], b: pr[1], child: node });
  };
  rec(bestPair[0]);
  rec(bestPair[1]);
  steps.push({ a: bestPair[0], b: bestPair[1], child: targetIdx });
  return { steps, owned: alreadyOwned };
}



export function loadOwned() {
  try {
    return new Set(JSON.parse(localStorage.getItem(OWNED_KEY) || "[]"));
  } catch {
    return new Set();
  }
}
export function saveOwned(set) {
  localStorage.setItem(OWNED_KEY, JSON.stringify([...set]));
}
